import React, {useEffect, useRef, useState} from "react";
import {
    Box,
    Grid,
    Typography,
    Button,
    IconButton,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    Divider,
    alpha,
    useTheme,
    Paper,
    Alert,
    Chip,
    Menu,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import {ApiDetails, KEY} from "../../../Constants";
import CryptoJS from "crypto-js";
import LeftSide from "./Left Side/LeftSide";
import RightSide from "./Right Side/RightSide";
import {ErrorMessage, Field, Form, Formik} from "formik";
import * as Yup from "yup";
import LoadingButton from "@mui/lab/LoadingButton";
import SendIcon from "@mui/icons-material/Send";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import BedIcon from '@mui/icons-material/Bed';
import HomeIcon from '@mui/icons-material/Home';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GroupIcon from '@mui/icons-material/Group';
import EmailIcon from '@mui/icons-material/Email';
import FlagIcon from '@mui/icons-material/Flag';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import BadgeIcon from '@mui/icons-material/Badge';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import {showSnackbar} from "../../../components/SnackBar/SnackBar";
import useGoBack from "../../../components/useGoBack";
import WaterDropIcon from "@mui/icons-material/Water";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BankStatementUploadModal from "./Bank Statement Upload/BankStatementUploadModal";
import { handle401Unauthorized, handle403Forbidden } from '../../../utils/authErrorHandler';
import { getUserData } from '../../../services/api/client';


// Enhanced Deposit Management Component (unchanged)
const DepositManagement = ({open, onClose, onSave}) => {
    const theme = useTheme();
    const [depositItems, setDepositItems] = useState([{description: '', amount: '', refundable: ''}]);
    const [totalDeposit, setTotalDeposit] = useState(0);
    const [refundableDeposit, setRefundableDeposit] = useState(0);
    const [depositSet, setDepositSet] = useState(false);

    const addDepositItem = () => {
        setDepositItems([...depositItems, {description: '', amount: '', refundable: ''}]);
    };

    const updateDepositItem = (index, field, value) => {
        const updatedItems = [...depositItems];
        updatedItems[index][field] = value;
        setDepositItems(updatedItems);
        setDepositSet(false);
    };

    const removeDepositItem = (index) => {
        if (depositItems.length > 1) {
            const updatedItems = depositItems.filter((_, i) => i !== index);
            setDepositItems(updatedItems);
            setDepositSet(false);
        }
    };

    const calculateDeposit = () => {
        try {
            let total = 0;
            let refundable = 0;

            depositItems.forEach(item => {
                if (!item.description || !item.amount || !item.refundable) {
                    throw new Error("Please fill in all deposit fields");
                }

                if (item.refundable.toUpperCase() !== "YES" && item.refundable.toUpperCase() !== "NO") {
                    throw new Error("Refundable field must be YES or NO");
                }

                const amount = parseFloat(item.amount);
                total += amount;

                if (item.refundable.toUpperCase() === "YES") {
                    refundable += amount;
                }
            });

            setTotalDeposit(total);
            setRefundableDeposit(refundable);
            setDepositSet(true);
        } catch (error) {
            showSnackbar({
                title: "Error!",
                message: error.message,
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }
    };

    const handleSave = () => {
        if (!depositSet) {
            showSnackbar({
                title: "Error!",
                message: "Please calculate deposit first",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        onSave({
            items: depositItems,
            totalDeposit,
            refundableDeposit
        });
        onClose();
    };

    const resetForm = () => {
        setDepositItems([{description: '', amount: '', refundable: ''}]);
        setTotalDeposit(0);
        setRefundableDeposit(0);
        setDepositSet(false);
    };

    useEffect(() => {
        if (!open) {
            resetForm();
        }
    }, [open]);

    const isMobileDeposit = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobileDeposit}
            PaperProps={{
                sx: {
                    borderRadius: isMobileDeposit ? 0 : 2,
                }
            }}
        >
            <DialogTitle sx={{
                backgroundColor: "#2C5283",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: isMobileDeposit ? '12px 16px' : '16px 24px',
            }}>
                <Typography variant={isMobileDeposit ? "subtitle1" : "h6"}>Add Deposit Details</Typography>
                <IconButton onClick={onClose} sx={{color: "white"}} size={isMobileDeposit ? "small" : "medium"}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{p: isMobileDeposit ? 2 : 3}}>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3}}>
                    <Typography variant="h6" fontWeight="bold">Deposit Items</Typography>
                    <Button
                        onClick={addDepositItem}
                        variant="contained"
                        size="small"
                        sx={{
                            background: `linear-gradient(45deg, #2C5283, #1E3A63)`,
                            textTransform: "none"
                        }}
                    >
                        Add Item
                    </Button>
                </Box>

                {depositItems.map((item, index) => (
                    <Box key={index} sx={{
                        mb: 3,
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        backgroundColor: '#f9f9f9'
                    }}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                            <Typography variant="subtitle1" fontWeight="medium">
                                Item {index + 1}
                            </Typography>
                            {depositItems.length > 1 && (
                                <Button
                                    onClick={() => removeDepositItem(index)}
                                    size="small"
                                    color="error"
                                    sx={{textTransform: "none"}}
                                >
                                    Remove
                                </Button>
                            )}
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={item.description}
                                    onChange={(e) => updateDepositItem(index, 'description', e.target.value)}
                                    size="small"
                                    placeholder="e.g., House Deposit"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Amount (KSH)"
                                    type="number"
                                    value={item.amount}
                                    onChange={(e) => updateDepositItem(index, 'amount', e.target.value)}
                                    size="small"
                                    placeholder="0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={item.refundable}
                                        onChange={(e) => updateDepositItem(index, 'refundable', e.target.value)}
                                        displayEmpty
                                    >
                                        <MenuItem value="">Select Refundable</MenuItem>
                                        <MenuItem value="YES">YES - Refundable</MenuItem>
                                        <MenuItem value="NO">NO - Non-refundable</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                ))}

                <Box sx={{mt: 3, mb: 2}}>
                    <Button
                        onClick={calculateDeposit}
                        variant="outlined"
                        fullWidth
                        sx={{
                            borderColor: "#2C5283",
                            color: "#2C5283",
                            '&:hover': {
                                borderColor: "#1E3A63",
                                backgroundColor: alpha("#2C5283", 0.04)
                            }
                        }}
                    >
                        Calculate Deposit
                    </Button>
                </Box>

                {depositSet && (
                    <Box sx={{
                        p: 3,
                        backgroundColor: alpha("#4CAF50", 0.1),
                        borderRadius: 2,
                        border: "1px solid #4CAF50"
                    }}>
                        <Typography variant="h6" fontWeight="bold" sx={{mb: 2, color: "#2E7D32"}}>
                            Deposit Summary
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">Total Deposit</Typography>
                                <Typography variant="h5" fontWeight="bold" color="success.main">
                                    KSH {totalDeposit.toLocaleString()}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">Refundable Amount</Typography>
                                <Typography variant="h5" fontWeight="bold" color="success.main">
                                    KSH {refundableDeposit.toLocaleString()}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Box sx={{mt: 2}}>
                            <Typography variant="body2" color="text.secondary">
                                Non-refundable: KSH {(totalDeposit - refundableDeposit).toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{
                p: isMobileDeposit ? 2 : 3,
                flexDirection: isMobileDeposit ? 'column' : 'row',
                gap: isMobileDeposit ? 1 : 0,
            }}>
                <Button
                    onClick={onClose}
                    color="inherit"
                    fullWidth={isMobileDeposit}
                    sx={{ order: isMobileDeposit ? 2 : 0 }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!depositSet}
                    fullWidth={isMobileDeposit}
                    sx={{
                        background: `linear-gradient(45deg, #4CAF50, #2E7D32)`,
                        '&:disabled': {
                            backgroundColor: '#ccc'
                        },
                        order: isMobileDeposit ? 1 : 0,
                    }}
                >
                    Save Deposit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const PropertyDetails = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const matches = useMediaQuery("(max-width:700px)");

    // Mobile menu state
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
    const mobileMenuOpen = Boolean(mobileMenuAnchor);

    const handleMobileMenuOpen = (event) => {
        setMobileMenuAnchor(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMenuAnchor(null);
    };

    let myMapDetailsDecrypted = "";

    try {
        myMapDetailsDecrypted = CryptoJS.AES.decrypt(localStorage.getItem("myMap"), KEY);
    } catch (e) {
        console.log(e);
    }

    const originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);

    // Helper function to get auth token
    const getAuthToken = () => {
        const userData = getUserData();
        return userData ? userData.find(([key]) => key === 'access_token')?.[1] : null;
    };

    const [tenants, setTenants] = useState([])
    const [property, setProperty] = useState({
        agency_id: "",
        apply_penalty: "",
        consumer_secret: "",
        electricity_bill: "",
        expected_income: "",
        ho_number: "",
        income_received: "",
        landlord_id: "",
        location: "",
        minimum_days_to_vacate: "",
        minimum_electricity_bill: "",
        minimum_water_bill: "",
        occupancy: "",
        other_charges: "",
        pass_key: "",
        payment_details: "",
        penalty_date: "",
        penalty_percentage: "",
        property_id: "",
        property_name: "",
        query_number: "",
        reminder_date: "",
        short_code: "",
        till_number: "",
        total_property_units: "",
        transaction_type: "",
        water_bill: "",
    });

    const [propertyImage, setPropertyImage] = useState(null);
    const [billTenantsModal, setBillTenantsModal] = useState(false);
    const [addTenantModal, setAddTenantModal] = useState(false);
    const [depositModal, setDepositModal] = useState(false);

    // Enhanced state management
    const [units, setUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [selectedUnitWaterReading, setSelectedUnitWaterReading] = useState(null);
    const [selectedUnitElectricityReading, setSelectedUnitElectricityReading] = useState(null);
    const [depositDetails, setDepositDetails] = useState(null);
    const [depositAsArrears, setDepositAsArrears] = useState(true);
    const [rentAsArrears, setRentAsArrears] = useState(true);
    const [tenantLoading, setTenantLoading] = useState(false);
    const [bankStatementModal, setBankStatementModal] = useState(false);

    // NEW: Airbnb-specific state
    const [isAirbnbUnit, setIsAirbnbUnit] = useState(false);
    const [bookingType, setBookingType] = useState('long_term');
    const [checkInDate, setCheckInDate] = useState('');
    const [checkOutDate, setCheckOutDate] = useState('');
    const [guestCount, setGuestCount] = useState(1);
    const [specialRequests, setSpecialRequests] = useState('');

    // Tenant lookup state
    const [lookupModal, setLookupModal] = useState(false);
    const [lookupResult, setLookupResult] = useState(null);
    const [lookingUp, setLookingUp] = useState(false);
    const [pendingFormValues, setPendingFormValues] = useState(null);

    // File uploads (keeping as null for now)
    const [tenantPhoto] = useState(null);
    const [photoOfNationalID] = useState(null);
    const [copyOfContract] = useState(null);
    const [inventoryChecklist] = useState(null);

    const initialValues = {
        first_name: "",
        last_name: "",
        phone_number: "",
        secondary_phone_number: "",
        national_id: "",
        rent: "",
        next_of_kin_first_name: "",
        next_of_kin_last_name: "",
        next_of_kin_relationship: "",
        next_of_kin_contacts: "",
        next_of_kin_national_id: "",
        occupation_or_profession: "",
        institution_name: "",
        place_of_work: "",
        reason_for_relocating: "",
        previous_residence: "",
        email_address: "",
        landlord_id: JSON.parse(originalText)[0][1],
    };

    // Validation schema for tenant form
    const tenantValidationSchema = Yup.object().shape({
        first_name: Yup.string()
            .min(2, "First name must be at least 2 characters")
            .required("First name is required"),
        last_name: Yup.string()
            .min(2, "Last name must be at least 2 characters")
            .required("Last name is required"),
        phone_number: Yup.string()
            .matches(/^[0-9]{10,12}$/, "Phone number must be 10-12 digits")
            .required("Phone number is required"),
        email_address: Yup.string()
            .email("Please enter a valid email address"),
        rent: Yup.number()
            .positive("Rent must be a positive number")
            .required("Rent is required"),
    });

    useEffect(() => {
        // Validate that we have required IDs before making API calls
        const landlordId = JSON.parse(originalText)[0]?.[1];
        const propertyId = props.property_id;

        if (!propertyId || !landlordId) {
            console.error("Missing required IDs:", {propertyId, landlordId});
            showSnackbar({
                title: "Error!",
                message: "Missing property or landlord information. Please try again.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        fetch(ApiDetails + "pegasus/visionary/property/get/Specific/property", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken() || ""
            },
            body: JSON.stringify({
                property_id: propertyId,
                landlord_id: landlordId,
            }),
        })
            .then(async (response) => {
                let data = await response.json();
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    setProperty(data);
                    // Fetch units when property is loaded
                    getUnits(data.property_id);
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed fetching property details.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Failed fetching property details.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });

        getPropertyPhoto()
    }, []);

    // Fetch tenants
    useEffect(() => {
        // Validate property ID and landlord ID before fetching tenants
        const propertyId = props.property_id;
        const landlordId = JSON.parse(originalText)[0]?.[1];

        if (!propertyId || propertyId === "") {
            console.error("Missing property ID for fetching tenants");
            return;
        }

        if (!landlordId || landlordId === "") {
            console.error("Missing landlord ID for fetching tenants");
            return;
        }


        console.log("Fetching tenants with:", {propertyId, landlordId}); // Debug log

        fetch(ApiDetails + "pegasus/visionary/tenant/get/property/tenants", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken() || ""
            },
            body: JSON.stringify({
                property_id: propertyId,
                landlord_id: landlordId  // Add landlord_id to request body
            }),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 403) {
                    console.error("Permission denied - 403 response:", data);
                    localStorage.clear();
                    window.location.href = "/";
                    return;
                }
                if (response.status === 200) {
                    setTenants(data);
                }
            })
            .catch((err) => {
                console.error("Error fetching tenants:", err);
            });
    }, [props.property_id]);

    const getPropertyPhoto = () => {
        // Validate property ID before fetching photo
        const propertyId = props.property_id;

        if (!propertyId) {
            console.error("Missing property ID for fetching photo");
            return;
        }

        fetch(ApiDetails + "pegasus/visionary/property/get/Specific/property/photo", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken() || ""
            },
            body: JSON.stringify({
                property_id: propertyId,
            }),
        })
            .then(async (response) => {
                let data = await response.json();
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    let pic = data.property_image;
                    if (pic.base64 !== "No File Found") {
                        setPropertyImage("data:image/jpeg;base64," + pic.base64);
                    }
                }
            })
            .catch((err) => {
                console.log(err);
            });
    }

    const getUnits = (propertyID) => {
        if (!propertyID) {
            console.log("Property ID is missing");
            return;
        }

        fetch(ApiDetails + "pegasus/visionary/property/getUnits", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken() || ""
            },
            body: JSON.stringify({property_id: propertyID}),
        })
            .then(async (response) => {
                let data = await response.json();
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    // Handle both old format (array) and new format (object with units array)
                    const unitsData = Array.isArray(data) ? data : (data.units || []);
                    setUnits(unitsData);
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Something went wrong loading units.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.error("Fetch error:", err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong loading units.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    // NEW: Helper function to get unit type chip
    const getUnitTypeChip = (unit) => {
        const unitType = unit.unit_type || 'long_term';
        if (unitType === 'short_term') {
            return (
                <Chip
                    size="small"
                    label="Airbnb"
                    color="error"
                    variant="outlined"
                    icon={<BedIcon fontSize="small"/>}
                    sx={{ml: 1, fontSize: '0.7rem'}}
                />
            );
        }
        return (
            <Chip
                size="small"
                label="Long Term"
                color="primary"
                variant="outlined"
                icon={<HomeIcon fontSize="small"/>}
                sx={{ml: 1, fontSize: '0.7rem'}}
            />
        );
    };

    // NEW: Calculate total cost for short-term bookings
    const calculateShortTermCost = () => {
        if (!checkInDate || !checkOutDate || !selectedUnit) return 0;

        const checkIn = new Date(checkInDate);
        const checkOut = new Date(checkOutDate);
        const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));

        if (nights <= 0) return 0;

        const nightlyRate = selectedUnit.nightly_rate || 0;
        const cleaningFee = selectedUnit.cleaning_fee || 0;

        return (nights * nightlyRate) + cleaningFee;
    };

    const handleClose = () => {
        setDepositModal(false);
        setAddTenantModal(false);
        // Reset form state
        setSelectedUnit(null);
        setSelectedUnitWaterReading(null);
        setSelectedUnitElectricityReading(null);
        setDepositDetails(null);
        setDepositAsArrears(true);
        setRentAsArrears(true);
        // NEW: Reset Airbnb state
        setIsAirbnbUnit(false);
        setBookingType('long_term');
        setCheckInDate('');
        setCheckOutDate('');
        setGuestCount(1);
        setSpecialRequests('');
    };

    const handleDepositSave = (deposit) => {
        setDepositDetails(deposit);
        setDepositModal(false);
        setAddTenantModal(true);
    };

    // NEW: Enhanced unit selection handler
    const handleUnitSelection = (unitData) => {
        setSelectedUnit(unitData);
        setSelectedUnitWaterReading(unitData.meter_reading);
        setSelectedUnitElectricityReading(unitData.electricity_meter_reading);

        // NEW: Check if this is an Airbnb unit
        const unitType = unitData.unit_type || 'long_term';
        setIsAirbnbUnit(unitType === 'short_term');

        // Set default booking type based on unit type
        if (unitType === 'short_term') {
            setBookingType('short_term');
        } else {
            setBookingType('long_term');
        }
    };

    const addTenant = async (values) => {
        setTenantLoading(true);

        // Validate landlord ID and property ID before proceeding
        const landlordId = JSON.parse(originalText)[0]?.[1];
        const propertyId = property.property_id;

        if (!landlordId || !propertyId) {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Missing landlord or property information. Please refresh and try again.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            console.error("Missing IDs:", {landlordId, propertyId});
            return;
        }

        // Existing validation
        if (values.first_name === "" || values.last_name === "") {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "First and last names are required",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (values.phone_number === "" || values.phone_number.length < 10) {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Enter a valid phone number",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (selectedUnit === null) {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Please select a unit",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (values.rent === "") {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Rent amount is required",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        // Additional validation for short-term bookings
        if (isAirbnbUnit && bookingType === 'short_term') {
            if (!checkInDate || !checkOutDate) {
                setTenantLoading(false);
                showSnackbar({
                    title: "Error!",
                    message: "Check-in and check-out dates are required for short-term bookings",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                return;
            }

            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);
            const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));

            if (nights < (selectedUnit.minimum_stay_nights || 1)) {
                setTenantLoading(false);
                showSnackbar({
                    title: "Error!",
                    message: `Minimum stay is ${selectedUnit.minimum_stay_nights || 1} nights`,
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                return;
            }

            if (selectedUnit.maximum_stay_nights && nights > selectedUnit.maximum_stay_nights) {
                setTenantLoading(false);
                showSnackbar({
                    title: "Error!",
                    message: `Maximum stay is ${selectedUnit.maximum_stay_nights} nights`,
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                return;
            }
        }

        // Deposit is only required for long-term rentals
        if ((!isAirbnbUnit || bookingType === 'long_term') && !depositDetails) {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Please add deposit details",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        // Store form values for later use by performAddTenant
        setPendingFormValues({...values});

        // Tenant lookup before adding
        const nationalId = values.national_id?.trim();
        const phoneNumber = values.phone_number?.trim();

        if (nationalId || phoneNumber) {
            setLookingUp(true);
            setTenantLoading(false);
            try {
                const lookupBody = {};
                if (nationalId) lookupBody.national_id = nationalId;
                if (phoneNumber) lookupBody.phone_number = phoneNumber;

                const response = await fetch(ApiDetails + "pegasus/visionary/tenant/lookup", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "token": getAuthToken() || ""
                    },
                    body: JSON.stringify(lookupBody),
                });

                if (response.status === 401) {
                    handle401Unauthorized();
                    setLookingUp(false);
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    setLookingUp(false);
                    return;
                }

                if (response.status === 200) {
                    const result = await response.json();
                    if (result.found) {
                        // Tenant exists in DB - show review screen
                        setLookupResult(result);
                        setLookupModal(true);
                        setLookingUp(false);
                        return;
                    }
                    // found: false - tenant not in DB, proceed directly
                    setLookingUp(false);
                    performAddTenant(values);
                    return;
                }

                // Non-200 response
                setLookingUp(false);
                showSnackbar({
                    title: "Error!",
                    message: "Tenant lookup service is unavailable. Please try again later.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                return;
            } catch (err) {
                console.log("Tenant lookup failed:", err);
                setLookingUp(false);
                showSnackbar({
                    title: "Error!",
                    message: "Could not verify tenant details. Please check your connection and try again.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                return;
            }
        }

        // No national_id or phone_number provided - proceed directly
        performAddTenant(values);
    };

    const performAddTenant = (values) => {
        const formValues = values || pendingFormValues;
        if (!formValues) return;

        setTenantLoading(true);
        setLookupModal(false);

        const landlordId = JSON.parse(originalText)[0]?.[1];
        const propertyId = property.property_id;

        // Prepare data (existing + new fields) - using validated IDs
        formValues.rent_as_arrear = rentAsArrears;
        formValues.deposit_as_arrears = depositAsArrears;
        formValues.is_student = "NO";
        formValues.landlord_email_address = JSON.parse(originalText)[5][1];
        formValues.landlord_id = landlordId;
        formValues.property_name = property.property_name;
        formValues.property_id = propertyId;
        formValues.meter_reading = selectedUnitWaterReading;
        formValues.electricity_meter_reading = selectedUnitElectricityReading;
        formValues.house_number = selectedUnit.unit;

        // Handle deposit based on booking type
        if (depositDetails) {
            formValues.deposit = depositDetails.items.map(item => [item.description, item.amount, item.refundable]);
            formValues.deposit_held = depositDetails.refundableDeposit;
        } else {
            formValues.deposit = [];
            formValues.deposit_held = 0;
        }

        // Add Airbnb-specific fields
        formValues.unit_type = selectedUnit.unit_type || 'long_term';
        formValues.booking_type = bookingType;
        formValues.airbnb_listing_id = selectedUnit.airbnb_listing_id || null;

        if (isAirbnbUnit && bookingType === 'short_term') {
            formValues.check_in_date = checkInDate;
            formValues.check_out_date = checkOutDate;
            formValues.guest_count = guestCount;
            formValues.special_requests = specialRequests;
            formValues.nightly_rate = selectedUnit.nightly_rate;
            formValues.cleaning_fee = selectedUnit.cleaning_fee;
            formValues.total_booking_cost = calculateShortTermCost();
        }

        let formData = new FormData();
        formData.append("body", JSON.stringify(formValues));
        formData.append("photo_of_national_id", photoOfNationalID);
        formData.append("copy_of_contract", copyOfContract);
        formData.append("inventory_checklist", inventoryChecklist);
        formData.append("tenant_photo", tenantPhoto);

        fetch(ApiDetails + "pegasus/visionary/tenant/add/tenant", {
            method: "POST",
            headers: {
                "token": getAuthToken() || ""
            },
            body: formData,
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    const successMessage = isAirbnbUnit && bookingType === 'short_term'
                        ? "Guest booking created successfully!"
                        : "Tenant added successfully.";

                    showSnackbar({
                        title: "Success!",
                        message: successMessage,
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    window.location.reload();
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Something went wrong. Please try again later",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setTenantLoading(false);
            })
            .catch((err) => {
                setTenantLoading(false);
                console.log(err)
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    const goBack = useGoBack();

    return (
        <>
            {/* Enhanced Deposit Management Dialog */}
            <DepositManagement
                open={depositModal}
                onClose={() => {
                    setDepositModal(false);
                    setAddTenantModal(true);
                }}
                onSave={handleDepositSave}
            />

            {/* Enhanced Add Tenant Dialog with Airbnb Support */}
            <Dialog
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                open={addTenantModal}
                keepMounted
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        overflow: 'visible',
                        margin: isMobile ? 0 : undefined,
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 1,
                    background: `linear-gradient(45deg, ${alpha("#2C5283", 0.1)}, ${alpha("#2C5283", 0.05)})`,
                    position: 'relative',
                    padding: isMobile ? '12px 16px' : '16px 24px',
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                        gap: isMobile ? 1 : 0,
                    }}>
                        <Box sx={{display: 'flex', alignItems: 'center', minWidth: 0}}>
                            <Box
                                sx={{
                                    backgroundColor: alpha("#2C5283", 0.1),
                                    p: isMobile ? 0.75 : 1,
                                    borderRadius: 2,
                                    mr: isMobile ? 1.5 : 2,
                                    color: "#2C5283"
                                }}
                            >
                                {isAirbnbUnit && bookingType === 'short_term' ?
                                    <BedIcon fontSize={isMobile ? "small" : "medium"}/> :
                                    <PersonAddIcon fontSize={isMobile ? "small" : "medium"}/>}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold" noWrap>
                                    {/* NEW: Dynamic title based on unit type */}
                                    {isAirbnbUnit && bookingType === 'short_term' ? 'Add New Guest' : 'Add New Tenant'}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                                    }}
                                >
                                    {property.property_name} • {property.location}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{
                    p: isMobile ? 2 : 3,
                    zoom: matches ? "85%" : "100%",
                    overflowX: 'hidden',
                }}>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={tenantValidationSchema}
                        onSubmit={addTenant}
                    >
                        {(formikProps) => (
                            <Form noValidate>
                                <Grid container spacing={3}>
                                    {/* Personal Details Section */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{mb: 2}}>
                                            {isAirbnbUnit && bookingType === 'short_term' ? 'Guest Details' : 'Personal Details'}
                                        </Typography>
                                        <Divider sx={{mb: 2}}/>
                                    </Grid>

                                    {["first_name", "last_name", "email_address", "phone_number", "secondary_phone_number", "national_id"].map((field, index) => (
                                        <Grid item xs={12} sm={field === "email_address" ? 6 : 4} key={index}>
                                            <Field
                                                as={TextField}
                                                type={field === "email_address" ? "email" : "text"}
                                                name={field}
                                                label={field.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                                                fullWidth
                                                variant="outlined"
                                                error={formikProps.errors[field] && formikProps.touched[field]}
                                                helperText={<ErrorMessage name={field}/>}
                                                required={["first_name", "last_name", "phone_number"].includes(field)}
                                                InputProps={field === "email_address" ? {
                                                    startAdornment: <EmailIcon sx={{color: "#2C5283", mr: 1}}/>
                                                } : {}}
                                            />
                                        </Grid>
                                    ))}

                                    {/* Rent field with dynamic label */}
                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            as={TextField}
                                            type="number"
                                            name="rent"
                                            label={isAirbnbUnit && bookingType === 'short_term' ? 'Nightly Rate (KSH)' : 'Monthly Rent (KSH)'}
                                            fullWidth
                                            variant="outlined"
                                            error={formikProps.errors.rent && formikProps.touched.rent}
                                            helperText={<ErrorMessage name="rent"/>}
                                            required
                                            InputProps={{
                                                startAdornment: isAirbnbUnit && bookingType === 'short_term' ? (
                                                    <NightsStayIcon sx={{color: "#FF5722", mr: 1}}/>
                                                ) : (
                                                    <AttachMoneyIcon sx={{color: "#4CAF50", mr: 1}}/>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    {/* NEW: Short-term booking specific fields */}
                                    {isAirbnbUnit && bookingType === 'short_term' && (
                                        <>
                                            <Grid item xs={12}>
                                                <Alert severity="info" sx={{mt: 1}}>
                                                    <Typography variant="body2">
                                                        <strong>Airbnb Unit Selected:</strong> Additional booking
                                                        details required for short-term rental.
                                                    </Typography>
                                                </Alert>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Check-in Date"
                                                    type="date"
                                                    value={checkInDate}
                                                    onChange={(e) => setCheckInDate(e.target.value)}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                    inputProps={{
                                                        min: new Date().toISOString().split('T')[0]
                                                    }}
                                                    required
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Check-out Date"
                                                    type="date"
                                                    value={checkOutDate}
                                                    onChange={(e) => setCheckOutDate(e.target.value)}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                    inputProps={{
                                                        min: checkInDate || new Date().toISOString().split('T')[0]
                                                    }}
                                                    required
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Number of Guests"
                                                    type="number"
                                                    value={guestCount}
                                                    onChange={(e) => setGuestCount(Math.max(1, parseInt(e.target.value) || 1))}
                                                    inputProps={{min: 1, max: 10}}
                                                    InputProps={{
                                                        startAdornment: <GroupIcon sx={{color: "#2196F3", mr: 1}}/>
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Special Requests"
                                                    value={specialRequests}
                                                    onChange={(e) => setSpecialRequests(e.target.value)}
                                                    placeholder="e.g., Early check-in, extra towels"
                                                />
                                            </Grid>

                                            {/* NEW: Booking Summary */}
                                            {checkInDate && checkOutDate && selectedUnit && (
                                                <Grid item xs={12}>
                                                    <Paper sx={{
                                                        p: 2,
                                                        backgroundColor: alpha(theme.palette.success.main, 0.1)
                                                    }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            Booking Summary
                                                        </Typography>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            mb: 1
                                                        }}>
                                                            <Typography variant="body2">
                                                                {Math.ceil((new Date(checkOutDate) - new Date(checkInDate)) / (1000 * 60 * 60 * 24))} nights
                                                                × KSH {selectedUnit.nightly_rate?.toLocaleString()}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                KSH {(Math.ceil((new Date(checkOutDate) - new Date(checkInDate)) / (1000 * 60 * 60 * 24)) * (selectedUnit.nightly_rate || 0)).toLocaleString()}
                                                            </Typography>
                                                        </Box>
                                                        {selectedUnit.cleaning_fee && (
                                                            <Box sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                mb: 1
                                                            }}>
                                                                <Typography variant="body2">Cleaning fee</Typography>
                                                                <Typography
                                                                    variant="body2">KSH {selectedUnit.cleaning_fee.toLocaleString()}</Typography>
                                                            </Box>
                                                        )}
                                                        <Divider sx={{my: 1}}/>
                                                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                                            <Typography variant="subtitle2">Total</Typography>
                                                            <Typography variant="subtitle2">
                                                                KSH {calculateShortTermCost().toLocaleString()}
                                                            </Typography>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                            )}
                                        </>
                                    )}

                                    {/* Property Selection */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{mb: 2, mt: 2}}>Property Details</Typography>
                                        <Divider sx={{mb: 2}}/>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" required>
                                            <Select
                                                value={selectedUnit || ""}
                                                onChange={(e) => handleUnitSelection(e.target.value)}
                                                displayEmpty
                                                disabled={!units || units.length === 0}
                                            >
                                                <MenuItem value="" disabled>Select Unit</MenuItem>
                                                {units && units.length > 0 ? (
                                                    units.map((unit, index) => (
                                                        <MenuItem
                                                            key={index}
                                                            value={unit}
                                                            disabled={unit.state === "OCCUPIED"}
                                                        >
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                width: '100%'
                                                            }}>
                                                                <Typography>{unit.unit}</Typography>
                                                                {getUnitTypeChip(unit)}
                                                                <Box component="span" sx={{
                                                                    ml: 'auto',
                                                                    color: unit.state === "OCCUPIED" ? "error.main" : "success.main",
                                                                    fontSize: "0.8rem"
                                                                }}>
                                                                    ({unit.state.toLowerCase()})
                                                                </Box>
                                                            </Box>
                                                        </MenuItem>
                                                    ))
                                                ) : (
                                                    <MenuItem disabled>No units available</MenuItem>
                                                )}
                                            </Select>
                                            <FormHelperText>
                                                {!units || units.length === 0 ? "No available units" : "Required"}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    {/* NEW: Booking Type Selection for Airbnb units */}
                                    {isAirbnbUnit && (
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <Select
                                                    value={bookingType}
                                                    onChange={(e) => setBookingType(e.target.value)}
                                                    displayEmpty
                                                >
                                                    <MenuItem value="short_term">
                                                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                            <BedIcon sx={{mr: 1, color: '#FF5722'}}/>
                                                            Short-term (Airbnb Guest)
                                                        </Box>
                                                    </MenuItem>
                                                    <MenuItem value="long_term">
                                                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                            <HomeIcon sx={{mr: 1, color: '#2196F3'}}/>
                                                            Long-term (Traditional Tenant)
                                                        </Box>
                                                    </MenuItem>
                                                </Select>
                                                <FormHelperText>
                                                    Choose how this unit will be rented
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>
                                    )}

                                    {/* Deposit section - Only for long-term rentals */}
                                    {(!isAirbnbUnit || bookingType === 'long_term') && (
                                        <Grid item xs={12} sm={6}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                onClick={() => {
                                                    setAddTenantModal(false);
                                                    setDepositModal(true);
                                                }}
                                                sx={{
                                                    height: 56,
                                                    textTransform: "none",
                                                    backgroundColor: depositDetails ? alpha("#4CAF50", 0.1) : "#FBF4F0",
                                                    borderColor: depositDetails ? "#4CAF50" : "#e0e0e0",
                                                    color: depositDetails ? "#4CAF50" : "#2C5283"
                                                }}
                                            >
                                                {depositDetails ?
                                                    `Deposit Set: KSH ${depositDetails.totalDeposit.toLocaleString()}` :
                                                    "Add Deposit Details"
                                                }
                                            </Button>
                                        </Grid>
                                    )}

                                    {/* Show deposit not required notice for Airbnb */}
                                    {isAirbnbUnit && bookingType === 'short_term' && (
                                        <Grid item xs={12}>
                                            <Alert severity="info">
                                                <Typography variant="body2">
                                                    <strong>No Deposit Required:</strong>
                                                </Typography>
                                            </Alert>
                                        </Grid>
                                    )}

                                    {/* Water and Electricity Meter Readings */}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Water Meter Reading"
                                            type="number"
                                            value={selectedUnitWaterReading || ''}
                                            onChange={(e) => setSelectedUnitWaterReading(e.target.value)}
                                            disabled={selectedUnitWaterReading === null}
                                            InputProps={{
                                                startAdornment: <WaterDropIcon sx={{color: "#2196F3", mr: 1}}/>
                                            }}
                                            placeholder={selectedUnitWaterReading === null ? "Select unit first" : "Enter reading"}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Electricity Meter Reading"
                                            type="number"
                                            value={selectedUnitElectricityReading || ''}
                                            onChange={(e) => setSelectedUnitElectricityReading(e.target.value)}
                                            disabled={selectedUnitElectricityReading === null}
                                            InputProps={{
                                                startAdornment: <ElectricBoltIcon sx={{color: "#FF9800", mr: 1}}/>
                                            }}
                                            placeholder={selectedUnitElectricityReading === null ? "Select unit first" : "Enter reading"}
                                        />
                                    </Grid>

                                    {/* Billing Options - Only show for long-term bookings */}
                                    {(!isAirbnbUnit || bookingType === 'long_term') && (
                                        <>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between'
                                                }}>
                                                    <Typography>Add Deposit As Arrears</Typography>
                                                    <Switch
                                                        checked={depositAsArrears}
                                                        onChange={(e) => setDepositAsArrears(e.target.checked)}
                                                    />
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between'
                                                }}>
                                                    <Typography>Add Rent As Arrears</Typography>
                                                    <Switch
                                                        checked={rentAsArrears}
                                                        onChange={(e) => setRentAsArrears(e.target.checked)}
                                                    />
                                                </Box>
                                            </Grid>
                                        </>
                                    )}

                                    {/* Next of Kin Details Section - Always show but less prominent for short-term */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{mb: 2, mt: 2}}>
                                            {isAirbnbUnit && bookingType === 'short_term' ? 'Emergency Contact (Optional)' : 'Next of Kin Details'}
                                        </Typography>
                                        <Divider sx={{mb: 2}}/>
                                    </Grid>

                                    {["next_of_kin_first_name", "next_of_kin_last_name", "next_of_kin_relationship", "next_of_kin_contacts", "next_of_kin_national_id"].map((field, index) => (
                                        <Grid item xs={12} sm={4} key={index}>
                                            <Field
                                                as={TextField}
                                                type="text"
                                                name={field}
                                                label={field.replace(/_/g, " ").replace("next of kin", "").replace(/\b\w/g, l => l.toUpperCase()).trim()}
                                                fullWidth
                                                variant="outlined"
                                                error={formikProps.errors[field] && formikProps.touched[field]}
                                                helperText={<ErrorMessage name={field}/>}
                                            />
                                        </Grid>
                                    ))}

                                    {/* Buttons */}
                                    <Grid item xs={12} container justifyContent="flex-end" sx={{mt: 2}}>
                                        <Button onClick={handleClose} color="inherit" sx={{mr: 2}}>
                                            Cancel
                                        </Button>
                                        <LoadingButton
                                            type="submit"
                                            loading={tenantLoading || lookingUp}
                                            variant="contained"
                                            startIcon={isAirbnbUnit && bookingType === 'short_term' ? <BedIcon/> :
                                                <PersonAddIcon/>}
                                            sx={{
                                                background: `linear-gradient(45deg, #2C5283, #1E3A63)`,
                                                px: 3
                                            }}
                                        >
                                            {lookingUp ? 'Checking...' : (isAirbnbUnit && bookingType === 'short_term' ? 'Create Booking' : 'Add Tenant')}
                                        </LoadingButton>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>

            {/* Tenant Lookup Review Modal */}
            <Dialog
                open={lookupModal}
                onClose={() => {
                    setLookupModal(false);
                    setLookupResult(null);
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{sx: {borderRadius: 3}}}
            >
                <DialogTitle sx={{
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: lookupResult?.flags?.is_flagged
                        ? 'linear-gradient(135deg, #fff5f5 0%, #ffe0e0 100%)'
                        : 'linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%)',
                    borderBottom: '1px solid #e0e0e0',
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <PersonSearchIcon sx={{color: '#2C5283'}}/>
                        <Typography variant="h6" fontWeight={600}>
                            Existing Tenant Found
                        </Typography>
                    </Box>
                    <IconButton onClick={() => {
                        setLookupModal(false);
                        setLookupResult(null);
                    }} size="small">
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{pt: 3}}>
                    {lookupResult && (
                        <Box>
                            {/* Flags Warning Banner */}
                            {lookupResult.flags?.is_flagged && (
                                <Alert
                                    severity="error"
                                    icon={<FlagIcon/>}
                                    sx={{mb: 3, fontWeight: 500}}
                                >
                                    This person has been flagged by{' '}
                                    <strong>{lookupResult.flags.flag_count}</strong> other landlord(s).
                                    Review the flags below before proceeding.
                                </Alert>
                            )}

                            {/* Tenant Details Section */}
                            <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1.5, color: '#2C5283', display: 'flex', alignItems: 'center', gap: 1}}>
                                <BadgeIcon fontSize="small"/> Tenant Details
                            </Typography>
                            <Box sx={{
                                p: 2,
                                bgcolor: '#fafafa',
                                borderRadius: 2,
                                border: '1px solid #e0e0e0',
                                mb: 3,
                            }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="textSecondary">Full Name</Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {lookupResult.tenant?.first_name} {lookupResult.tenant?.last_name}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="textSecondary">National ID</Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {lookupResult.tenant?.national_id || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                            <PhoneIcon sx={{fontSize: 16, color: '#666'}}/>
                                            <Typography variant="caption" color="textSecondary">Phone Number</Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight={500}>
                                            {lookupResult.tenant?.phone_number || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                            <EmailIcon sx={{fontSize: 16, color: '#666'}}/>
                                            <Typography variant="caption" color="textSecondary">Email</Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight={500}>
                                            {lookupResult.tenant?.email_address || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    {lookupResult.tenant?.occupation_or_profession && (
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                <WorkIcon sx={{fontSize: 16, color: '#666'}}/>
                                                <Typography variant="caption" color="textSecondary">Occupation</Typography>
                                            </Box>
                                            <Typography variant="body1" fontWeight={500}>
                                                {lookupResult.tenant.occupation_or_profession}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {lookupResult.tenant?.place_of_work && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="textSecondary">Place of Work</Typography>
                                            <Typography variant="body1" fontWeight={500}>
                                                {lookupResult.tenant.place_of_work}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>

                            {/* KYC - National ID Photos */}
                            {lookupResult.kyc && (lookupResult.kyc.national_id_front?.base64 || lookupResult.kyc.national_id_back?.base64) && (
                                <>
                                    <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1.5, color: '#2C5283', display: 'flex', alignItems: 'center', gap: 1}}>
                                        <VerifiedUserIcon fontSize="small"/> KYC - National ID
                                    </Typography>
                                    <Grid container spacing={2} sx={{mb: 3}}>
                                        {lookupResult.kyc.national_id_front?.base64 && lookupResult.kyc.national_id_front.base64 !== "No File Found" && (
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    bgcolor: '#fafafa',
                                                }}>
                                                    <Typography variant="caption" sx={{
                                                        display: 'block',
                                                        p: 1,
                                                        bgcolor: '#e3f2fd',
                                                        color: '#1565c0',
                                                        fontWeight: 600,
                                                        textAlign: 'center',
                                                    }}>
                                                        ID - Front
                                                    </Typography>
                                                    <Box sx={{p: 1, textAlign: 'center'}}>
                                                        <img
                                                            src={`data:image/${lookupResult.kyc.national_id_front.extension || 'jpeg'};base64,${lookupResult.kyc.national_id_front.base64}`}
                                                            alt="National ID Front"
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: 200,
                                                                objectFit: 'contain',
                                                                borderRadius: 4,
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        )}
                                        {lookupResult.kyc.national_id_back?.base64 && lookupResult.kyc.national_id_back.base64 !== "No File Found" && (
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    bgcolor: '#fafafa',
                                                }}>
                                                    <Typography variant="caption" sx={{
                                                        display: 'block',
                                                        p: 1,
                                                        bgcolor: '#e3f2fd',
                                                        color: '#1565c0',
                                                        fontWeight: 600,
                                                        textAlign: 'center',
                                                    }}>
                                                        ID - Back
                                                    </Typography>
                                                    <Box sx={{p: 1, textAlign: 'center'}}>
                                                        <img
                                                            src={`data:image/${lookupResult.kyc.national_id_back.extension || 'jpeg'};base64,${lookupResult.kyc.national_id_back.base64}`}
                                                            alt="National ID Back"
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: 200,
                                                                objectFit: 'contain',
                                                                borderRadius: 4,
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </>
                            )}

                            {/* Flags Section */}
                            {lookupResult.flags?.is_flagged && lookupResult.flags.flags?.length > 0 && (
                                <>
                                    <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1.5, color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1}}>
                                        <FlagIcon fontSize="small"/> Flags ({lookupResult.flags.flag_count})
                                    </Typography>
                                    {lookupResult.flags.flags.map((flag, index) => {
                                        const categoryColors = {
                                            ABSCONDED: '#d32f2f',
                                            MISBEHAVIOR: '#e65100',
                                            PROPERTY_DAMAGE: '#bf360c',
                                            UNPAID_BALANCE: '#f9a825',
                                            OTHER: '#757575',
                                        };
                                        const categoryLabels = {
                                            ABSCONDED: 'Absconded',
                                            MISBEHAVIOR: 'Misbehavior',
                                            PROPERTY_DAMAGE: 'Property Damage',
                                            UNPAID_BALANCE: 'Unpaid Balance',
                                            OTHER: 'Other',
                                        };
                                        return (
                                            <Box key={index} sx={{
                                                mb: 1.5,
                                                p: 2,
                                                bgcolor: '#fff5f5',
                                                borderRadius: 1.5,
                                                borderLeft: `4px solid ${categoryColors[flag.category] || '#757575'}`,
                                            }}>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                                                    <Chip
                                                        label={categoryLabels[flag.category] || flag.category}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: categoryColors[flag.category] || '#757575',
                                                            color: '#fff',
                                                            fontWeight: 600,
                                                            fontSize: '11px',
                                                        }}
                                                    />
                                                    <Typography variant="body2" color="textSecondary">
                                                        by {flag.flagged_by_name} on{' '}
                                                        {new Date(flag.created_at).toLocaleDateString('en-US', {
                                                            year: 'numeric', month: 'short', day: 'numeric'
                                                        })}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{fontStyle: 'italic', color: '#555'}}>
                                                    "{flag.reason}"
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </>
                            )}

                            {/* No flags - positive indicator */}
                            {!lookupResult.flags?.is_flagged && (
                                <Alert severity="success" icon={<VerifiedUserIcon/>} sx={{mt: 1}}>
                                    No flags found on this person. You may proceed.
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{p: 2, borderTop: '1px solid #e0e0e0'}}>
                    <Button
                        onClick={() => {
                            setLookupModal(false);
                            setLookupResult(null);
                        }}
                        variant="outlined"
                        color="inherit"
                        sx={{textTransform: 'none', borderRadius: 2}}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            setLookupResult(null);
                            performAddTenant();
                        }}
                        variant="contained"
                        color={lookupResult?.flags?.is_flagged ? "error" : "primary"}
                        startIcon={<PersonAddIcon/>}
                        sx={{textTransform: 'none', borderRadius: 2}}
                    >
                        {lookupResult?.flags?.is_flagged ? 'Proceed Anyway' : 'Proceed & Add Tenant'}
                    </Button>
                </DialogActions>
            </Dialog>

            <BankStatementUploadModal
                open={bankStatementModal}
                onClose={() => setBankStatementModal(false)}
                propertyId={property.property_id}
                landlordId={JSON.parse(originalText)[0][1]} // Make sure you have this available
                ApiDetails={ApiDetails}
                originalText={originalText}
            />

            {/*<Box bgcolor="#F7F9FA">*/}
            <Box>
                {/* Header Section */}
                <Box
                    sx={{
                        position: "sticky",
                        top: isMobile ? 56 : 65,
                        zIndex: 1000,
                        backgroundColor: "#F5F5F5",
                        borderBottom: "2px solid #2C5283",
                        padding: isMobile ? "8px 12px" : "10px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: isMobile ? "wrap" : "nowrap",
                        gap: isMobile ? 1 : 0,
                    }}
                >
                    {/* Left Section: Back button and Title */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        flex: isMobile ? "1 1 auto" : "0 0 auto",
                        minWidth: 0,
                    }}>
                        <IconButton
                            sx={{
                                color: "#2C5283",
                                marginRight: 1,
                                backgroundColor: "#F4F6F8",
                                padding: isMobile ? "6px" : "8px",
                            }}
                            onClick={goBack}
                            size={isMobile ? "small" : "medium"}
                        >
                            <ArrowBackIcon fontSize={isMobile ? "small" : "medium"}/>
                        </IconButton>
                        <Typography
                            variant={isMobile ? "subtitle1" : "h6"}
                            sx={{
                                fontWeight: "bold",
                                color: "#2C5283",
                                whiteSpace: isMobile ? "nowrap" : "normal",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: isMobile ? "0.9rem" : "1.25rem",
                            }}
                        >
                            {isMobile
                                ? property.property_name.toUpperCase()
                                : `View Property - ${property.property_name.toUpperCase()}`
                            }
                        </Typography>
                    </Box>

                    {/* Right Section: Action Buttons - Desktop */}
                    {!isMobile && (
                        <Box sx={{display: "flex", gap: 1, pt: "5px", pb: "5px"}}>
                            <Button
                                variant="outlined"
                                sx={{textTransform: "none", backgroundColor: "#FBF4F0"}}
                                onClick={() => setAddTenantModal(true)}
                            >
                                Add Tenant
                            </Button>
                            <Button
                                variant="outlined"
                                sx={{
                                    textTransform: "none",
                                    backgroundColor: "#E8F5E8",
                                    borderColor: "#4CAF50",
                                    color: "#2E7D32",
                                    '&:hover': {
                                        backgroundColor: "#C8E6C9",
                                        borderColor: "#388E3C"
                                    }
                                }}
                                onClick={() => setBankStatementModal(true)}
                                startIcon={<CloudUploadIcon/>}
                            >
                                Upload Bank Statement
                            </Button>
                            <Button
                                variant="outlined"
                                sx={{textTransform: "none", backgroundColor: "#FBF4F0"}}
                                onClick={() => window.location.reload()}
                            >
                                Refresh Details
                            </Button>
                        </Box>
                    )}

                    {/* Mobile Menu Button */}
                    {isMobile && (
                        <>
                            <IconButton
                                onClick={handleMobileMenuOpen}
                                sx={{
                                    color: "#2C5283",
                                    backgroundColor: "#F4F6F8",
                                    padding: "6px",
                                }}
                                size="small"
                            >
                                <MenuIcon fontSize="small"/>
                            </IconButton>
                            <Menu
                                anchorEl={mobileMenuAnchor}
                                open={mobileMenuOpen}
                                onClose={handleMobileMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                PaperProps={{
                                    sx: {
                                        minWidth: 200,
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                        borderRadius: '8px',
                                    }
                                }}
                            >
                                <MenuItem
                                    onClick={() => {
                                        setAddTenantModal(true);
                                        handleMobileMenuClose();
                                    }}
                                >
                                    Add Tenant
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        setBankStatementModal(true);
                                        handleMobileMenuClose();
                                    }}
                                >
                                    Upload Bank Statement
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        window.location.reload();
                                    }}
                                >
                                    Refresh Details
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </Box>

                {/* Main Layout */}
                <Grid container spacing={isMobile ? 1 : 3} sx={{ flexDirection: isMobile ? 'column-reverse' : 'row' }}>
                    {/* Left Section: Property Profile Card */}
                    <Grid item xs={12} md={4} lg={3}>
                        <LeftSide propertyDetails={property} propertyImage={propertyImage}/>
                    </Grid>

                    {/* Right Section: Tabs and Content */}
                    <Grid item xs={12} md={8} lg={9}>
                        <RightSide
                            propertyDetails={property}
                            tenants={tenants}
                            setBillTenantsModal={setBillTenantsModal}
                            billTenantsModal={billTenantsModal}
                        />
                    </Grid>
                </Grid>
            </Box>
        </>
    )
}

export default PropertyDetails;